import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ITicket } from "../ticket-module/models/ITicket";
import { getAllTickets, getAllDepartments, reassignTicket, updateTicketStatus, addMessage, getUserListByRole, getDepartmentUsers, getTicketDashboardStatistics, ITicketDashboardStats } from "../ticket-module/services/ticketService";
import { getUserToken } from "../utils/Util";
import TicketCard from "../ticket-module/components/TicketCard";
import CreateTicketModal from "../ticket-module/components/CreateTicketModal";
import { LoginPopup } from "../components/LoginPopup";

interface Department { id: string; name: string; description: string; }

type FilterTab = "tumu" | "havuz" | "benim" | "takip" | "kapali";
const FILTER_TABS: FilterTab[] = ["tumu", "havuz", "benim", "takip", "kapali"];

const STATUS_IDS = {
  atamaBekleyen: "c94f0b39-47cf-43ea-9c06-a886943c1030",
  beklemede:     "fb939613-6149-4799-9837-a6ae823b8983",
  tamamlanan:    "b69b62f7-3800-458a-bcc0-e1a72bc7a9b8",
  acik:          "1ab0e936-3557-4c2a-8d8c-dc1900bf464b",
  kapali:        "c11e154e-6839-40be-9c9d-8209693034f4",
};

const TAB_LABELS: { key: FilterTab; label: string; shortLabel: string; icon: string }[] = [
  { key: "tumu",   label: "Tümü",                    shortLabel: "Tümü",   icon: "bi-grid-3x3-gap-fill" },
  { key: "havuz",  label: "Ticket Havuzu",           shortLabel: "Havuz",  icon: "bi-inbox-fill" },
  { key: "benim",  label: "Bana Atanan Ticketler",   shortLabel: "Atanan", icon: "bi-person-fill" },
  { key: "takip",  label: "Takip Ettiklerim",        shortLabel: "Takip",  icon: "bi-eye-fill" },
  { key: "kapali", label: "Kapalı Ticketler",        shortLabel: "Kapalı", icon: "bi-lock-fill" },
];

// tumu: kapalı olmayan tüm ticketlar
// havuz: kimseye atanmamış ticketlar
// benim: bana atanmış ticketlar
// takip: benim yarattığım ama başka birine atadığım VEYA izleme listesinde olduğum (benim üzerime atanmamış) ticketlar
// kapali: kapatılmış ticketlar
function matchTab(ticket: ITicket, tab: FilterTab, currentUserId: string): boolean {
  if (tab === "havuz") return !ticket.customerId?.trim();
  if (tab === "benim") return ticket.customerId === currentUserId;
  if (tab === "takip") {
    if (ticket.customerId === currentUserId) return false; // zaten "Bana Atanan Ticketler"de
    const createdByMeAssignedToOther = ticket.mainCustomerId === currentUserId && !!ticket.customerId?.trim();
    const isWatching = ticket.assignments?.some((a) => a.assignedUserId === currentUserId) ?? false;
    return createdByMeAssignedToOther || isWatching;
  }
  if (tab === "kapali") return ticket.ticketStatusId === STATUS_IDS.kapali;
  return true;
}

export default function Destek() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as FilterTab | null;
  const initialTab: FilterTab = tabFromUrl && FILTER_TABS.includes(tabFromUrl) ? tabFromUrl : "tumu";

  const [allTickets, setAllTickets]   = useState<ITicket[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [activeTab, setActiveTab]     = useState<FilterTab>(initialTab);
  const [search, setSearch]           = useState("");
  const [showCreate, setShowCreate]   = useState(false);
  const [pageIndex, setPageIndex]     = useState(0);
  const [paging, setPaging]           = useState<{ totalPages: number; totalCount: number; hasNextPage: boolean; hasPreviousPage: boolean } | null>(null);
  const [dashStats, setDashStats]     = useState<ITicketDashboardStats | null>(null);

  // "Kapalı" tab — kendi state'i ve sayfalaması (diğer tabların rozet sayılarını bozmasın diye allTickets'tan bağımsız)
  const [closedTicketsList, setClosedTicketsList] = useState<ITicket[]>([]);
  const [closedPageIndex, setClosedPageIndex]     = useState(0);
  const [closedPaging, setClosedPaging]           = useState<{ totalPages: number; totalCount: number; hasNextPage: boolean; hasPreviousPage: boolean } | null>(null);

  // Search popup
  const [showSearch, setShowSearch]   = useState(false);
  const [searchStatusId, setSearchStatusId]       = useState("");
  const [searchDeptId, setSearchDeptId]           = useState("");
  const [searchFromDate, setSearchFromDate]       = useState("");
  const [searchToDate, setSearchToDate]           = useState("");
  const [searchTerm, setSearchTerm]               = useState("");
  // Active filter params (persisted across pagination)
  const [activeFilters] = useState<{ statusId?: string; departmentId?: string; fromDate?: string; toDate?: string; searchTerm?: string }>({});

  const currentUserId = getUserToken()?.userId ?? "";
  const [userList, setUserList]   = useState<{ id: string; name: string }[]>([]);
  const [myDeptIds, setMyDeptIds] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [compactView, setCompactView] = useState(false);

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleTakeTicket = async (ticketId: string) => {
    try {
      let users = userList;
      if (users.length === 0) {
        const res = await getUserListByRole();
        users = (res.data ?? []).map((u: any) => ({ id: u.id, name: `${u.customerName} ${u.customerSurName}` }));
        setUserList(users);
      }
      await reassignTicket(ticketId, currentUserId);
      const TAKE_STATUS_ID = "c94f0b39-47cf-43ea-9c06-a886943c1030";
      await updateTicketStatus(ticketId, TAKE_STATUS_ID);
      const currentUserName = users.find((u) => u.id === currentUserId)?.name ?? "Bir yetkili";
      await addMessage(ticketId, `@TicketBilgi ${currentUserName} bu ticketi üzerine aldı`);
      loadTickets(pageIndex);
    } catch {}
  };

  const loadTickets = (
    page = pageIndex,
    filters?: { statusId?: string; departmentId?: string; fromDate?: string; toDate?: string; searchTerm?: string },
  ) => {
    const f = filters ?? activeFilters;
    setLoading(true);
    Promise.all([
      getAllTickets({ pageIndex: page, pageSize: 50, ...f }),
      getTicketDashboardStatistics(),
    ])
      .then(([allRes, statsRes]) => {
        setAllTickets(allRes.data?.items ?? []);
        setPaging(allRes.data?.paging ?? null);
        setDashStats(statsRes.data ?? null);
      })
      .catch((err) => {
        setError("Ticketlar yüklenemedi.");
        if (err?.response?.status === 401) setShowLoginPopup(true);
      })
      .finally(() => setLoading(false));
  };

  const loadClosedTickets = (page = closedPageIndex) => {
    setLoading(true);
    getAllTickets({ pageIndex: page, pageSize: 50, statusId: STATUS_IDS.kapali })
      .then((res) => {
        setClosedTicketsList(res.data?.items ?? []);
        setClosedPaging(res.data?.paging ?? null);
      })
      .catch(() => setError("Ticketlar yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTickets(0, {});
    if (initialTab === "kapali") loadClosedTickets(0);
    getAllDepartments()
      .then((res) => setDepartments(res.data ?? []))
      .catch(() => {});
    const YAZILIM_DEPT_ID = "a1adf997-e85a-40da-823c-7477e0d2cf09";
    const DESTEK_DEPT_ID  = "c631c065-2de5-4327-acfa-57767f390820";
    Promise.all([
      getDepartmentUsers(YAZILIM_DEPT_ID),
      getDepartmentUsers(DESTEK_DEPT_ID),
    ]).then(([yazilimRes, destekRes]) => {
      const depts: string[] = [];
      if ((yazilimRes.data ?? []).includes(currentUserId)) depts.push(YAZILIM_DEPT_ID);
      if ((destekRes.data  ?? []).includes(currentUserId)) depts.push(DESTEK_DEPT_ID);
      setMyDeptIds(depts);
    }).catch(() => {});
  }, []);

  // allTickets kapatılmamış ticketları içerir ("Kapalı" tabı kendi ayrı state'ini kullanır)
  const nonClosedTickets = allTickets.filter((t) => t.ticketStatusId !== STATUS_IDS.kapali);

  const filtered = nonClosedTickets.filter((t) => {
    const matchesTab = matchTab(t, activeTab, currentUserId);
    const matchesSearch = search === "" ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.mainCustomerName.toLowerCase().includes(search.toLowerCase()) ||
      t.ticketDepartmentName.toLowerCase().includes(search.toLowerCase()) ||
      (t.ticketNo ?? "").toLowerCase().includes(search.toLowerCase());
    return matchesTab && matchesSearch;
  }).sort((a, b) => b.priority - a.priority);

  // Group by department (sadece "Tümü" görünümünde kullanılır)
  const grouped = departments.reduce<Record<string, ITicket[]>>((acc, dept) => {
    acc[dept.id] = filtered.filter((t) => t.ticketDepartmentId === dept.id);
    return acc;
  }, {});
  // Tickets with unknown dept (safeguard)
  const knownDeptIds = new Set(departments.map((d) => d.id));
  const ungrouped = filtered.filter((t) => !knownDeptIds.has(t.ticketDepartmentId));

  // Stats — total API'den; havuz/benim/takip her zaman listeden (dashStats.mine ve
  // dashStats.waitingForAssignment backend'de farklı kavramları temsil ediyor, gösterilen
  // listeyle birebir örtüşmüyor)
  const total       = dashStats?.total ?? nonClosedTickets.length;
  const havuzCount  = nonClosedTickets.filter((t) => matchTab(t, "havuz", currentUserId)).length;
  const myCount     = nonClosedTickets.filter((t) => matchTab(t, "benim", currentUserId)).length;
  const takipCount  = nonClosedTickets.filter((t) => matchTab(t, "takip", currentUserId)).length;
  const kapaliCount = dashStats?.statusCounts?.find((s) => s.statusId === STATUS_IDS.kapali)?.count
    ?? closedPaging?.totalCount
    ?? closedTicketsList.length;


  return (
    <div className="destek-page" style={{ padding: isMobile ? "16px 12px" : "24px" }}>

      {/* Header */}
      <div className="destek-header">
        <div>
          <h4 style={{ fontWeight: 800, color: "#111827", marginBottom: "4px" }}>
            <i className="bi bi-headset me-2 text-primary"></i>Destek Talepleri
          </h4>
          <p style={{ color: "#6b7280", fontSize: "0.88rem", margin: 0 }}>
            Tüm destek ticketlarını buradan takip edebilirsiniz.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/ticketSettings")}
            title="Ticket Ayarları"
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 18px", borderRadius: 12, border: "1.5px solid #e5e7eb",
              background: "#fff", color: "#374151", fontWeight: 700, fontSize: "0.88rem",
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f3f4f6"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
          >
            <i className="bi bi-gear-fill" style={{ fontSize: "0.95rem" }}></i>
            {!isMobile && "Ticket Ayarları"}
          </button>
          <button
            onClick={() => setCompactView(v => !v)}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 18px", borderRadius: 12,
              border: `1.5px solid ${compactView ? "#6366f1" : "#e5e7eb"}`,
              background: compactView ? "#eef2ff" : "#fff",
              color: compactView ? "#4f46e5" : "#6b7280",
              fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", transition: "all 0.15s",
            }}
          >
            <i className={`bi ${compactView ? "bi-grid-fill" : "bi-grid"}`} style={{ fontSize: "0.95rem" }}></i>
            {!isMobile && "Özet Görünüm"}
          </button>
          <button
            onClick={() => setShowSearch(true)}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 18px", borderRadius: 12, border: "1.5px solid #6366f1",
              background: "#fff", color: "#6366f1", fontWeight: 700, fontSize: "0.88rem",
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#eef2ff"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
          >
            <i className="bi bi-search" style={{ fontSize: "0.9rem" }}></i>
            {!isMobile && "Ticket Ara"}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="destek-create-btn"
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 6px 20px rgba(99,102,241,0.5)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 14px rgba(99,102,241,0.4)"; }}
          >
            <i className="bi bi-plus-circle-fill" style={{ fontSize: "1rem" }}></i>
            {!isMobile && "Yeni Ticket Yarat"}
          </button>
        </div>
      </div>

      <CreateTicketModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); loadTickets(0); }}
      />

      {/* Ticket Ara popup */}
      {showSearch && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowSearch(false); }}
        >
          <div style={{
            background: "#fff", borderRadius: 18, padding: "28px 28px 22px",
            width: "min(96vw, 480px)", boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="bi bi-search" style={{ color: "#6366f1" }}></i>
                Ticket Ara
              </div>
              <button
                type="button"
                onClick={() => setShowSearch(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.2rem", lineHeight: 1 }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* SearchTerm */}
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                  Arama Terimi
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ticket başlığı veya ticket no..."
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 10,
                    border: "1.5px solid #e5e7eb", fontSize: "0.85rem",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Status */}
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                  Durum
                </label>
                <select
                  value={searchStatusId}
                  onChange={(e) => setSearchStatusId(e.target.value)}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 10,
                    border: "1.5px solid #e5e7eb", fontSize: "0.85rem",
                    outline: "none", background: "#fff", cursor: "pointer",
                  }}
                >
                  <option value="">Tüm Durumlar</option>
                  <option value={STATUS_IDS.acik}>Açık</option>
                  <option value={STATUS_IDS.atamaBekleyen}>Atama Bekleyen</option>
                  <option value={STATUS_IDS.beklemede}>Beklemede</option>
                  <option value={STATUS_IDS.tamamlanan}>Tamamlanan</option>
                  <option value={STATUS_IDS.kapali}>Kapalı Ticketler</option>
                </select>
              </div>

              {/* Department */}
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                  Departman
                </label>
                <select
                  value={searchDeptId}
                  onChange={(e) => setSearchDeptId(e.target.value)}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 10,
                    border: "1.5px solid #e5e7eb", fontSize: "0.85rem",
                    outline: "none", background: "#fff", cursor: "pointer",
                  }}
                >
                  <option value="">Tüm Departmanlar</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* Date range */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={searchFromDate}
                    onChange={(e) => setSearchFromDate(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 10,
                      border: "1.5px solid #e5e7eb", fontSize: "0.85rem",
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                    Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    value={searchToDate}
                    onChange={(e) => setSearchToDate(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 10,
                      border: "1.5px solid #e5e7eb", fontSize: "0.85rem",
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setSearchStatusId("");
                  setSearchDeptId("");
                  setSearchFromDate("");
                  setSearchToDate("");
                  setSearchTerm("");
                }}
                style={{
                  padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb",
                  background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
                }}
              >
                Temizle
              </button>
              <button
                type="button"
                onClick={() => {
                  // Sonuçlar ayrı sayfada gösterilir; kapalı ticketler de dahil gelir.
                  const qs = new URLSearchParams();
                  if (searchTerm.trim()) qs.set("searchTerm", searchTerm.trim());
                  if (searchStatusId)    qs.set("statusId", searchStatusId);
                  if (searchDeptId)      qs.set("departmentId", searchDeptId);
                  if (searchFromDate)    qs.set("fromDate", searchFromDate);
                  if (searchToDate)      qs.set("toDate", searchToDate);
                  setShowSearch(false);
                  navigate(`/ticketSearch?${qs.toString()}`);
                }}
                style={{
                  padding: "9px 22px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 7,
                }}
              >
                <i className="bi bi-search"></i> Ara
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="destek-stat-grid" style={{ gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(5, 1fr)" }}>
        {[
          { label: "Toplam",             value: total,       color: "#6366f1", bg: "#eef2ff", icon: "bi-ticket-perforated-fill" },
          { label: "Ticket Havuzu",      value: havuzCount,  color: "#0891b2", bg: "#f0f9ff", icon: "bi-inbox-fill" },
          { label: "Bana Atanan",        value: myCount,     color: "#7c3aed", bg: "#f5f3ff", icon: "bi-person-fill" },
          { label: "Takip Ettiklerim",   value: takipCount,  color: "#d97706", bg: "#fffbeb", icon: "bi-eye-fill" },
          { label: "Kapalı",             value: kapaliCount, color: "#6b7280", bg: "#f9fafb", icon: "bi-lock-fill" },
        ].map((s) => (
          <div key={s.label} className="destek-stat-card" style={{ background: s.bg }}>
            <div className="destek-stat-card-header">
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: s.color }}>{s.label}</span>
              <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: "1rem" }}></i>
            </div>
            <span style={{ fontSize: "1.6rem", fontWeight: 800, color: "#111827" }}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Search + Tabs */}
      <div className="destek-filter-box">
        {/* Search bar */}
        <div className="destek-search-bar">
          <div style={{ position: "relative" }}>
            <i className="bi bi-search" style={{
              position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
              color: "#9ca3af", fontSize: "0.9rem",
            }}></i>
            <input
              type="text"
              placeholder="Ticket başlığı, müşteri adı veya departman ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="destek-search-input"
            />
          </div>
        </div>

        {/* Status tabs */}
        <div className="destek-tabs" style={{ overflowX: isMobile ? "auto" : "visible" }}>
          {TAB_LABELS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setSearchParams({ tab: tab.key }, { replace: true });
                  setPageIndex(0);
                  if (tab.key === "kapali") {
                    setClosedPageIndex(0);
                    loadClosedTickets(0);
                  } else {
                    loadTickets(0, {});
                  }
                }}
                className={`destek-tab-btn${isActive ? " active" : ""}`}
                style={{
                  flexShrink: 0,
                  flex: isMobile ? "none" : 1,
                  padding: isMobile ? "10px 12px" : "11px 8px",
                  fontSize: isMobile ? "0.78rem" : "0.85rem",
                }}
              >
                <i className={`bi ${tab.icon} me-1`}></i>
                {isMobile ? tab.shortLabel : tab.label}
                <span className={`destek-tab-badge${isActive ? " active" : ""}`}>
                  {tab.key === "tumu" ? total : tab.key === "havuz" ? havuzCount : tab.key === "benim" ? myCount : tab.key === "takip" ? takipCount : kapaliCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="destek-state-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>Yükleniyor...</p>
        </div>
      ) : error ? (
        <div className="destek-state-center" style={{ color: "#dc2626" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2rem" }}></i>
          <p style={{ marginTop: "8px" }}>{error}</p>
        </div>
      ) : activeTab === "kapali" ? (
        /* Kapalı tab — kendi ayrı state'inden, client-side filtreleme yok */
        closedTicketsList.length === 0 ? (
          <div className="destek-state-center">
            <i className="bi bi-inbox" style={{ fontSize: "2.5rem", color: "#d1d5db" }}></i>
            <p style={{ color: "#9ca3af", marginTop: "10px", fontSize: "0.9rem" }}>Henüz ticket yok.</p>
          </div>
        ) : (
          <div style={compactView ? { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 } : {}}>
            {closedTicketsList.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} currentUserId={currentUserId} currentUserDeptIds={myDeptIds} onTake={handleTakeTicket} compact={compactView} />)}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="destek-state-center">
          <i className="bi bi-inbox" style={{ fontSize: "2.5rem", color: "#d1d5db" }}></i>
          <p style={{ color: "#9ca3af", marginTop: "10px", fontSize: "0.9rem" }}>
            {search ? "Arama sonucu bulunamadı." : "Henüz ticket yok."}
          </p>
        </div>
      ) : activeTab !== "tumu" ? (
        /* Havuz / Bana Atanan / Takip — düz liste */
        <div style={compactView ? { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 } : {}}>
          {filtered.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} currentUserId={currentUserId} currentUserDeptIds={myDeptIds} onTake={handleTakeTicket} compact={compactView} />)}
        </div>
      ) : (
        /* Tümü — departmana göre gruplu */
        <div>
          {departments.map((dept) => {
            const items = grouped[dept.id] ?? [];
            if (items.length === 0) return null;
            return (
              <div key={dept.id} style={{ marginBottom: "28px" }}>
                <div className="destek-dept-heading">
                  <div className="destek-dept-icon">
                    <i className="bi bi-diagram-3-fill text-white" style={{ fontSize: "0.85rem" }}></i>
                  </div>
                  <div>
                    <span style={{ fontWeight: 800, fontSize: "0.97rem", color: "#111827" }}>{dept.name}</span>
                    <span className="destek-dept-badge">{items.length} ticket</span>
                  </div>
                  <div className="destek-dept-divider" />
                </div>
                <div style={compactView ? { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 } : {}}>
                  {items.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} currentUserId={currentUserId} currentUserDeptIds={myDeptIds} onTake={handleTakeTicket} compact={compactView} />)}
                </div>
              </div>
            );
          })}
          {ungrouped.length > 0 && (
            <div style={{ marginBottom: "28px" }}>
              <div className="destek-dept-heading">
                <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#6b7280" }}>Diğer</span>
                <div className="destek-dept-divider" />
              </div>
              <div style={compactView ? { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 } : {}}>
                {ungrouped.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} currentUserId={currentUserId} currentUserDeptIds={myDeptIds} onTake={handleTakeTicket} compact={compactView} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination for allTickets (Tümü / Havuz / Bana Atanan / Takip) */}
      {activeTab !== "kapali" && paging && paging.totalPages > 1 && (
        <div className="destek-pagination">
          <button
            onClick={() => { const p = pageIndex - 1; setPageIndex(p); loadTickets(p, activeFilters); }}
            disabled={!paging.hasPreviousPage || loading}
            className="destek-page-btn"
            style={{
              padding: "8px 16px",
              background: paging.hasPreviousPage ? "#fff" : "#f9fafb",
              color: paging.hasPreviousPage ? "#374151" : "#d1d5db",
              cursor: paging.hasPreviousPage ? "pointer" : "not-allowed",
            }}
          >
            <i className="bi bi-chevron-left me-1"></i>Önceki
          </button>

          {Array.from({ length: paging.totalPages }, (_, i) => i).map((i) => (
            <button
              key={i}
              onClick={() => { setPageIndex(i); loadTickets(i, activeFilters); }}
              className={`destek-page-btn${pageIndex === i ? " active" : ""}`}
              style={{
                width: "38px", height: "38px",
                background: pageIndex === i ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#fff",
                color: pageIndex === i ? "#fff" : "#374151",
              }}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => { const p = pageIndex + 1; setPageIndex(p); loadTickets(p, activeFilters); }}
            disabled={!paging.hasNextPage || loading}
            className="destek-page-btn"
            style={{
              padding: "8px 16px",
              background: paging.hasNextPage ? "#fff" : "#f9fafb",
              color: paging.hasNextPage ? "#374151" : "#d1d5db",
              cursor: paging.hasNextPage ? "pointer" : "not-allowed",
            }}
          >
            Sonraki<i className="bi bi-chevron-right ms-1"></i>
          </button>

          <span className="destek-pagination-info">
            {paging.totalCount} ticket · Sayfa {pageIndex + 1} / {paging.totalPages}
          </span>
        </div>
      )}

      {/* Pagination for closedTicketsList ("kapali" tab) */}
      {activeTab === "kapali" && closedPaging && closedPaging.totalPages > 1 && (
        <div className="destek-pagination">
          <button
            onClick={() => { const p = closedPageIndex - 1; setClosedPageIndex(p); loadClosedTickets(p); }}
            disabled={!closedPaging.hasPreviousPage || loading}
            className="destek-page-btn"
            style={{
              padding: "8px 16px",
              background: closedPaging.hasPreviousPage ? "#fff" : "#f9fafb",
              color: closedPaging.hasPreviousPage ? "#374151" : "#d1d5db",
              cursor: closedPaging.hasPreviousPage ? "pointer" : "not-allowed",
            }}
          >
            <i className="bi bi-chevron-left me-1"></i>Önceki
          </button>

          {Array.from({ length: closedPaging.totalPages }, (_, i) => i).map((i) => (
            <button
              key={i}
              onClick={() => { setClosedPageIndex(i); loadClosedTickets(i); }}
              className={`destek-page-btn${closedPageIndex === i ? " active" : ""}`}
              style={{
                width: "38px", height: "38px",
                background: closedPageIndex === i ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#fff",
                color: closedPageIndex === i ? "#fff" : "#374151",
              }}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => { const p = closedPageIndex + 1; setClosedPageIndex(p); loadClosedTickets(p); }}
            disabled={!closedPaging.hasNextPage || loading}
            className="destek-page-btn"
            style={{
              padding: "8px 16px",
              background: closedPaging.hasNextPage ? "#fff" : "#f9fafb",
              color: closedPaging.hasNextPage ? "#374151" : "#d1d5db",
              cursor: closedPaging.hasNextPage ? "pointer" : "not-allowed",
            }}
          >
            Sonraki<i className="bi bi-chevron-right ms-1"></i>
          </button>

          <span className="destek-pagination-info">
            {closedPaging.totalCount} ticket · Sayfa {closedPageIndex + 1} / {closedPaging.totalPages}
          </span>
        </div>
      )}

      {showLoginPopup && (
        <LoginPopup
          onSuccess={() => { setShowLoginPopup(false); window.location.reload(); }}
        />
      )}
    </div>
  );
}
